import {
  useSearchExamEventsWithQuestionnairesQuery,
  type QuestionnaireTemplateId,
} from '@exam-center/graphql';
import { Flex, NoRowsOverlay } from '@shared/components';
import { dayjs } from '@shared/utils';
import { useMemo } from 'react';

import {
  AdminQuestionnaireEmptyStateOverlay,
  AdminQuestionnaireExportDataGrid,
} from '../../components';
import { useQuestionnaireExportSearchParams } from '../../hooks';
import { QuestionnaireExportButton } from '../questionnaire-export-button';

import { mapAdminQuestionnaireExportItemToRow } from './utils';

export default function AdminQuestionnaireExportList() {
  const [
    { examCenters, startDate, endDate, questionnaireTemplate, examCourses, page, pageSize },
    { changePageNumber, changePageSize },
  ] = useQuestionnaireExportSearchParams();

  const startDateFormatted = startDate ? dayjs(startDate) : null;
  const endDateFormatted = endDate ? dayjs(endDate) : null;

  const hasAllFilters =
    examCenters.length > 0 &&
    !!startDate &&
    !!endDate &&
    !!questionnaireTemplate &&
    examCourses.length > 0;

  const { data: examEventData, loading: isLoading } = useSearchExamEventsWithQuestionnairesQuery({
    variables: {
      searchExamEventsWithQuestionnairesInput: {
        examCenterCodes: examCenters.includes('all') ? null : examCenters,
        startDate: startDateFormatted?.startOf('day').toDate() ?? new Date(0),
        endDate: endDateFormatted?.endOf('day').toDate() ?? new Date(0),
        examCourseCodes: examCourses.includes('all') ? null : examCourses,
        templateId: questionnaireTemplate as QuestionnaireTemplateId,
        paging: {
          currentPage: page,
          pageSize,
        },
      },
    },
    skip: !hasAllFilters,
  });

  const rows = useMemo(
    () =>
      (examEventData?.ecSearchExamEventsWithQuestionnaires.data ?? []).map((item) =>
        mapAdminQuestionnaireExportItemToRow(item),
      ),
    [examEventData],
  );

  const total = examEventData?.ecSearchExamEventsWithQuestionnaires.total ?? 0;

  const isSearchEmpty = !hasAllFilters;

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4), flexGrow: 1 })}>
      <Flex sx={{ justifyContent: 'flex-end' }}>
        <QuestionnaireExportButton disabled={total === 0} />
      </Flex>
      <AdminQuestionnaireExportDataGrid
        rows={rows}
        loading={isLoading}
        sortingMode='server'
        sx={{ flexGrow: 1 }}
        slots={{
          noRowsOverlay: isSearchEmpty ? AdminQuestionnaireEmptyStateOverlay : NoRowsOverlay,
        }}
        hideFooter={total === 0}
        paginationMode='server'
        rowCount={total}
        pageSizeOptions={[10, 25, 50]}
        slotProps={{
          footer: {
            total,
            pagination: {
              page,
              count: total,
              rowsPerPage: pageSize,
              onPageChange: (_, newPage) => changePageNumber(newPage),
              onRowsPerPageChange: (e) => changePageSize(parseInt(e.target.value)),
            },
          },
        }}
      />
    </Flex>
  );
}
